/**
 * Reports & Analytics — a first-class dashboard widget (Admin/HR only).
 *
 * Cross-module overview: date-range filter, KPI tiles compared against the
 * previous period of equal length, a 14-day attendance chart, leave,
 * profitability and headcount cards, generated key insights, PDF export
 * (print) and saved views (per-user presets). Rendered inside the standard
 * `.dash-widget` glass shell, so it inherits the dashboard's glassmorphism +
 * corner radius and is draggable / reorderable in Personalise mode like any
 * other widget.
 */
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "../../utils/toast";
import {
  BarChart3, Printer, Save, CalendarDays, Users, Trash2, ArrowRight,
} from "lucide-react";
import api from "../../api/axios";
import { T } from "../../components/em/ui";
import WidgetHeader from "../dashboard/widgets/WidgetHeader";
import "./reports.css";

const money = (n) => "₹" + Math.round(Number(n) || 0).toLocaleString("en-IN");
// Indian short form for table cells: ₹3.2L, ₹1.1Cr, ₹45K.
const moneyShort = (n) => {
  const v = Number(n) || 0;
  const a = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (a >= 1e7) return `${sign}₹${(a / 1e7).toFixed(1)}Cr`;
  if (a >= 1e5) return `${sign}₹${(a / 1e5).toFixed(1)}L`;
  if (a >= 1e3) return `${sign}₹${(a / 1e3).toFixed(1)}K`;
  return `${sign}₹${Math.round(a)}`;
};
const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const todayStr = () => ymd(new Date());
const yearStartStr = () => `${new Date().getFullYear()}-01-01`;
const fmtRange = (a, b) => {
  const f = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return `${f(a)} → ${f(b)}`;
};
// The period of equal length ending the day before `from` — the comparison
// baseline for the KPI deltas.
const prevRange = (from, to) => {
  const a = new Date(`${from}T00:00:00`);
  const b = new Date(`${to}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return null;
  const days = Math.round((b - a) / 86400000) + 1;
  const pTo = new Date(a);
  pTo.setDate(a.getDate() - 1);
  const pFrom = new Date(pTo);
  pFrom.setDate(pTo.getDate() - (days - 1));
  return { from: ymd(pFrom), to: ymd(pTo) };
};
// 8.2 → "8h 12m"
const fmtHours = (h) => {
  const mins = Math.round((Number(h) || 0) * 60);
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
};
const signed = (n, digits = 1) => {
  const v = Math.round(n * 10 ** digits) / 10 ** digits;
  return `${v > 0 ? "+" : ""}${v}`;
};
const sumDays = (lv) => Math.round((lv?.by_type || []).reduce((s, t) => s + (Number(t.days) || 0), 0) * 10) / 10;

const Card = ({ icon: Icon, title, accent, aside, children }) => (
  <div className="report-card">
    <div className="report-card-head">
      {Icon && (
        <span className="report-card-icon" style={{ background: (accent || T.orange) + "1f" }}>
          <Icon size={15} color={accent || T.orange} />
        </span>
      )}
      <span className="report-card-title">{title}</span>
      {aside}
    </div>
    {children}
  </div>
);

// KPI tile with a coloured delta line underneath.
const Tile = ({ label, value, delta, tone }) => (
  <div className="report-tile">
    <span className="report-tile-label">{label}</span>
    <span className="report-tile-value">{value}</span>
    {delta && <span className={`report-tile-delta is-${tone || "neutral"}`}>{delta}</span>}
  </div>
);

const DAILY_SERIES = [
  { key: "present", label: "Present" },
  { key: "on_leave", label: "Leave" },
  { key: "absent", label: "Absent" },
];

// Stacked daily bars: present at the base, leave, then absent on top. Days
// with no check-ins (weekends, holidays) render as a flat stub.
const DailyChart = ({ days }) => {
  const max = Math.max(1, ...days.map((d) => d.present + d.on_leave + d.absent));
  return (
    <div className="report-daily">
      {days.map((d) => {
        const total = d.present + d.on_leave + d.absent;
        const label = new Date(`${d.date}T00:00:00`);
        const tip = total
          ? `${label.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}: ${d.present} present · ${d.on_leave} on leave · ${d.absent} absent`
          : `${label.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}: no check-ins`;
        return (
          <div key={d.date} className="report-daily-col" title={tip}>
            <div className="report-daily-plot">
              {total ? (
                <div className="report-daily-bar" style={{ height: `${(total / max) * 100}%` }}>
                  {[...DAILY_SERIES].reverse().map((s) =>
                    d[s.key] > 0 ? (
                      <span key={s.key} className={`report-daily-seg is-${s.key}`} style={{ flexGrow: d[s.key] }} />
                    ) : null
                  )}
                </div>
              ) : (
                <span className="report-daily-stub" />
              )}
            </div>
            <span className="report-daily-label">{label.getDate()}</span>
          </div>
        );
      })}
    </div>
  );
};

// Margin colour bands for the profitability table.
const marginTone = (m) => (m < 0 ? "is-bad" : m < 15 ? "is-warn" : "is-good");

// Plain-language observations generated from the overview (and the previous
// period, when loaded). Each is { tone, text }.
const buildInsights = (data, prev) => {
  const out = [];
  const att = data.attendance;
  const pAtt = prev?.attendance;
  if (pAtt && pAtt.days > 0) {
    const d = att.attendance_rate - pAtt.attendance_rate;
    out.push(
      d >= 0
        ? { tone: "good", text: `Attendance is up ${Math.abs(d).toFixed(1)}% on the previous period.` }
        : { tone: "warn", text: `Attendance is down ${Math.abs(d).toFixed(1)}% on the previous period.` }
    );
  } else if (att.days > 0) {
    out.push({ tone: "good", text: `Attendance rate is ${att.attendance_rate}% across ${att.days} working day(s).` });
  }

  const projects = (data.workspace?.projects || []).filter((p) => p.revenue > 0);
  if (projects.length) {
    const worst = projects.reduce((a, b) => (b.margin < a.margin ? b : a));
    if (worst.margin < 15) {
      out.push({ tone: worst.margin < 0 ? "bad" : "warn", text: `${worst.name} margin is at ${worst.margin}%; review logged hours.` });
    } else {
      const best = projects.reduce((a, b) => (b.margin > a.margin ? b : a));
      out.push({ tone: "good", text: `Every project is above 15% margin; ${best.name} leads at ${best.margin}%.` });
    }
  }

  const today = data.leave?.today;
  if (today) {
    out.push({
      tone: "info",
      text: today.count
        ? `${today.count} ${today.count === 1 ? "employee" : "employees"} on leave today${today.top_department ? `; ${today.top_department} most affected` : ""}.`
        : "No one is on leave today.",
    });
  }

  const pending = data.leave?.by_status?.pending || 0;
  if (pending) {
    out.push({ tone: "warn", text: `${pending} leave ${pending === 1 ? "request is" : "requests are"} waiting for approval.` });
  }
  return out;
};

const Kpi = ({ label, value, color }) => (
  <div>
    <div style={{ font: `800 20px/1 ${T.font}`, color: color || T.fg1, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{value}</div>
    <div style={{ font: `500 11px ${T.font}`, color: T.fg7, marginTop: 4 }}>{label}</div>
  </div>
);
const KpiRow = ({ items }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 12, marginBottom: 14 }}>
    {items.map((k) => <Kpi key={k.label} {...k} />)}
  </div>
);

const BarList = ({ rows, max, color, fmt }) => {
  const m = max || Math.max(1, ...rows.map((r) => r.value));
  if (!rows.length) return <div style={{ font: `400 12px ${T.font}`, color: T.fg7 }}>No data in this range.</div>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      {rows.map((r) => (
        <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ font: `500 12px ${T.font}`, color: T.fg3, width: 110, minWidth: 110, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.label}</span>
          <div className="report-bar-track">
            <div className="report-bar-fill" style={{ width: `${Math.max(2, (r.value / m) * 100)}%`, background: r.color || color || T.orange }} />
          </div>
          <span style={{ font: `700 12px ${T.font}`, color: T.fg1, width: 70, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt ? fmt(r.value) : r.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function ReportsPanel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [prev, setPrev] = useState(null);
  const [from, setFrom] = useState(yearStartStr());
  const [to, setTo] = useState(todayStr());
  const [views, setViews] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const p = prevRange(from, to);
    try {
      // The baseline is best-effort: without it the tiles just omit deltas.
      const [res, prevRes] = await Promise.all([
        api.get(`/reports/overview?from=${from}&to=${to}`),
        p ? api.get(`/reports/overview?from=${p.from}&to=${p.to}`).catch(() => null) : null,
      ]);
      setData(res.data);
      setPrev(prevRes?.data || null);
    } catch {
      toast.error("Could not load reports");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  const loadViews = useCallback(async () => {
    try { const res = await api.get("/reports/views"); setViews(res.data || []); } catch {}
  }, []);

  // Always-on widget: load on mount + whenever the range changes; views once.
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadViews(); }, [loadViews]);

  const exportPdf = () => {
    document.body.classList.add("report-print-mode");
    const cleanup = () => {
      document.body.classList.remove("report-print-mode");
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  };

  const saveView = async () => {
    const name = window.prompt("Name this report view:", "");
    if (!name || !name.trim()) return;
    try {
      await api.post("/reports/views", { name: name.trim(), config: { from, to } });
      toast.success("View saved");
      loadViews();
    } catch { toast.error("Could not save view"); }
  };

  const applyView = (id) => {
    const v = views.find((x) => String(x.id) === String(id));
    if (v?.config?.from) setFrom(v.config.from);
    if (v?.config?.to) setTo(v.config.to);
  };

  const deleteView = async (id) => {
    try { await api.delete(`/reports/views/${id}`); toast.success("View deleted"); loadViews(); }
    catch { toast.error("Could not delete view"); }
  };

  const att = data?.attendance;
  const lv = data?.leave;
  const wsd = data?.workspace;
  const hc = data?.headcount;
  const pAtt = prev?.attendance;
  const hasPrevAtt = pAtt && pAtt.days > 0;
  const leaveTaken = sumDays(lv);
  const prevLeave = sumDays(prev?.leave);
  const net = hc ? hc.joiners - hc.exits : 0;
  const VS = "vs prev. period";

  return (
    <div className="reports-widget">
      <WidgetHeader icon={BarChart3} title="Reports & Analytics" />

      {/* Controls — single row: from · to-icon · end · Save view · Export PDF */}
      <div className="report-no-print reports-controls">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="reports-ctrl" aria-label="From date" />
        <ArrowRight size={15} color={T.fg7} className="reports-ctrl-arrow" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="reports-ctrl" aria-label="To date" />
        <div className="reports-ctrl-spacer" />
        <button type="button" onClick={saveView} className="reports-ctrl reports-btn-ghost">
          <Save size={14} /> Save view
        </button>
        <button type="button" onClick={exportPdf} className="reports-btn-primary">
          <Printer size={14} /> Export PDF
        </button>
      </div>

      {/* Saved-view chips (apply + delete) */}
      {views.length > 0 && (
        <div className="report-no-print reports-chips">
          {views.map((v) => (
            <span key={v.id} className="reports-chip">
              <button type="button" onClick={() => applyView(v.id)} className="reports-chip-apply">{v.name}</button>
              <button type="button" onClick={() => deleteView(v.id)} title="Delete view" className="reports-chip-del"><Trash2 size={12} /></button>
            </span>
          ))}
        </div>
      )}

      {/* Printable report area */}
      <div className="report-print-area">
        <div className="report-print-header" style={{ marginBottom: 14 }}>
          <div style={{ font: `700 18px ${T.font}`, color: T.fg1 }}>Reports &amp; Analytics</div>
          <div style={{ font: `400 12px ${T.font}`, color: T.fg5 }}>{fmtRange(from, to)}</div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: T.fg7, font: `400 13px ${T.font}` }}>Loading…</div>
        ) : !data ? (
          <div style={{ padding: 40, textAlign: "center", color: T.fg7, font: `400 13px ${T.font}` }}>No data.</div>
        ) : (
          <div className="reports-stack">
            {/* KPI tiles — deltas against the previous period of equal length */}
            <div className="report-tiles">
              <Tile
                label="Attendance rate"
                value={`${att.attendance_rate}%`}
                delta={hasPrevAtt ? `${signed(att.attendance_rate - pAtt.attendance_rate)}% ${VS}` : null}
                tone={hasPrevAtt && att.attendance_rate < pAtt.attendance_rate ? "bad" : "good"}
              />
              <Tile
                label="Avg. work hours"
                value={fmtHours(att.avg_hours)}
                delta={hasPrevAtt ? `${signed(att.avg_hours - pAtt.avg_hours)}h ${VS}` : null}
                tone={hasPrevAtt && att.avg_hours < pAtt.avg_hours ? "bad" : "good"}
              />
              <Tile
                label="Leave taken"
                value={`${leaveTaken} days`}
                delta={prevLeave > 0 ? `${signed(((leaveTaken - prevLeave) / prevLeave) * 100, 0)}% ${VS}` : null}
                tone="info"
              />
              <Tile
                label="Headcount"
                value={hc.active_headcount}
                delta={`${signed(net, 0)} net this period`}
                tone={net < 0 ? "bad" : "good"}
              />
            </div>

            {/* Daily attendance — last 14 days */}
            <Card
              title="Daily attendance · last 14 days"
              aside={
                <span className="report-legend">
                  {DAILY_SERIES.map((s) => (
                    <span key={s.key} className={`report-legend-item is-${s.key}`}>{s.label}</span>
                  ))}
                </span>
              }
            >
              <DailyChart days={att.daily || []} />
            </Card>

            {/* Leave */}
            <Card icon={CalendarDays} title="Leave summary & liability" accent={T.purple}>
              <KpiRow items={[
                { label: "Outstanding (days)", value: lv.liability.outstanding_days, color: T.purple },
                { label: "Taken YTD", value: lv.liability.taken_ytd_days },
                { label: "Upcoming", value: `${lv.upcoming_days}d` },
              ]} />
              <div style={{ font: `600 11px ${T.font}`, color: T.fg5, margin: "4px 0 8px" }}>Approved days by type</div>
              <BarList rows={(lv.by_type || []).map((t) => ({ label: t.name, value: t.days, color: t.color }))} fmt={(v) => `${v}d`} />
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                <Pill label={`${lv.by_status.approved} approved`} bg={T.success50} fg={T.success600} />
                <Pill label={`${lv.by_status.pending} pending`} bg="#fef3c7" fg="#92400e" />
                <Pill label={`${lv.by_status.rejected} rejected`} bg="#fee2e2" fg="#b91c1c" />
              </div>
            </Card>

            {/* Workspace */}
            <div className="report-card report-card--flush">
              <div className="report-card-pad">
                <div className="report-card-head">
                  <span className="report-card-title">Project profitability</span>
                </div>
                <div className="report-money">
                  <div className="report-money-box">
                    <span>Revenue</span>
                    <b style={{ color: T.success600 }}>{money(wsd.totals.revenue)}</b>
                  </div>
                  <div className="report-money-box">
                    <span>Cost</span>
                    <b style={{ color: T.danger }}>{money(wsd.totals.cost)}</b>
                  </div>
                  <div className="report-money-box">
                    <span>Margin</span>
                    <b style={{ color: T.orange }}>{wsd.totals.margin}%</b>
                  </div>
                </div>
              </div>
              {(wsd.projects || []).length ? (
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Hours</th>
                      <th>Cost</th>
                      <th>Revenue</th>
                      <th>Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wsd.projects.slice(0, 6).map((p) => (
                      <tr key={p.id || p.name}>
                        <td>{p.name}</td>
                        <td>{Math.round(p.total_hours || 0)}h</td>
                        <td>{moneyShort(p.cost)}</td>
                        <td>{moneyShort(p.revenue)}</td>
                        <td className={marginTone(p.margin)}>{p.revenue > 0 ? `${Math.round(p.margin)}%` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="report-card-pad report-empty">No project activity in this range.</div>
              )}
              {wsd.totals.pending_hours > 0 && (
                <div className="report-card-pad report-footnote">{wsd.totals.pending_hours}h pending approval (excluded)</div>
              )}
            </div>

            {/* Headcount */}
            <Card icon={Users} title="Headcount & workforce" accent={T.info600}>
              <KpiRow items={[
                { label: "Active", value: hc.active_headcount },
                { label: "Joiners", value: hc.joiners, color: T.success600 },
                { label: "Exits", value: hc.exits, color: T.danger },
              ]} />
              <div style={{ font: `600 11px ${T.font}`, color: T.fg5, margin: "4px 0 8px" }}>By department</div>
              <BarList rows={(hc.by_department || []).map((d) => ({ label: d.label, value: d.count }))} color={T.info} />
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                {(hc.by_type || []).map((t) => <Pill key={t.label} label={`${t.label}: ${t.count}`} bg={T.bgHead} fg={T.fg5} />)}
              </div>
            </Card>

            {/* Key insights */}
            {(() => {
              const insights = buildInsights(data, prev);
              return insights.length ? (
                <Card title="Key insights">
                  <ul className="report-insights">
                    {insights.map((i) => (
                      <li key={i.text} className={`is-${i.tone}`}>{i.text}</li>
                    ))}
                  </ul>
                </Card>
              ) : null;
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

const Pill = ({ label, bg, fg }) => (
  <span style={{ padding: "3px 10px", borderRadius: 9999, background: bg, color: fg, font: `600 11px ${T.font}`, whiteSpace: "nowrap" }}>{label}</span>
);
